import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { WeekFilter, MonthFilter, LocationFilter, StatusFilter, CommonSelectFilter } from './components/index';
import classes from './GlobalFilters.module.scss';
import DayFilter from './components/DayFilter/DayFilter';
import { appAreas, jobFilters, responsiveSizeTimesheet, tAndEFilters } from '../../../../utils/constants';
import ContractorFilter from './components/ContractorFilter/ContractorFilter';
import ClaimFilter from './components/ClaimFilter/ClaimFilter';
import MainButton from '../../buttons/MainButton';
import { buttonVariants } from '../../buttons/types';
import DatePostedFilter from './components/DatePostedFilter/DatePostedFilter';
import { filterTypes } from '../types';
import { TNE_SORT_BY, generalEnums } from '../../../../utils/enums';
import { useWindowSize } from '../../../../utils/hooks';

export default function GlobalFilters({
  filters,
  values,
  changeHandlers,
  part = appAreas.TIMESHEETS,
  isContainerBg = false,
  reset = false,
  resetHandler,
  resetIndividual,
  footerHandler,
  resetVisibility = false
}) {
  const { t } = useTranslation();
  const { width } = useWindowSize();

  const filtersOptions = useSelector((state) => state.filtersOptionsFromApi.filters);
  const filtersOptionsPending = useSelector((state) => state.filtersOptionsFromApi.isPending);
  const isApprover = useSelector((state) => state.accessToParts.tAndEApprover);
  const industries = useSelector((state) => state.constFromApi.industries);
  const contractTypes = useSelector((state) => state.constFromApi.contractTypes);
  const functions = useSelector((state) => state.constFromApi.functionsDirections);
  const workLocationTypes = useSelector((state) => state.constFromApi.workLocationType);
  const filterPeriods = useSelector((state) => state.constFromApi.filterPeriods);
  const jobFilterCounts = useSelector((state) => state.jobs.filterCount);
  const pendingFilterCount = useSelector((state) => state.jobs.pendingFilterCount);

  const [locations, setLocations] = useState([t('common.general.all')]);
  const [years, setYears] = useState([[]]);
  const [months, setMonths] = useState([[]]);
  const [days, setDays] = useState([[]]);
  const [sortby, setSortBy] = useState([[]]);
  const [avaliableStatuses, setAvaliableStatuses] = useState([]);
  const [contractors, setContractors] = useState([t('common.general.all')]);
  const [claims, setClaims] = useState([t('common.general.all')]);
  const [reArrangedFilter, setReArrangedFilter] = useState(filters);

  const containerRef = useRef(null);

  const getFilteredValues = (type) => {
    switch (type) {
      case filterTypes.YEAR:
        return filtersOptions?.Years?.map((item) => item?.Year);
      case filterTypes.MONTHS:
        return filtersOptions?.Months?.map((item) => item?.MonthName || item?.Month);
      case filterTypes.DAY:
        return filtersOptions?.Days?.map((item) => dayjs(item?.Day).format(generalEnums.DATEFORMAT));
      case filterTypes.LOCATIONS:
        return filtersOptions?.Locations
          ? [t('common.general.all'), ...filtersOptions.Locations.map((item) => item.Name)].filter(
              (list) => list !== ''
            )
          : [];
      case filterTypes.STATUS:
        return filtersOptions?.Status?.map((item) => item?.Status);
      case filterTypes.SORTBY:
        return filtersOptions?.SortBy?.map((item) => item);
      default:
        return null;
    }
  };

  const getListsForFilters = async () => {
    switch (part) {
      case appAreas.TIMESHEETS: {
        setLocations(getFilteredValues(filterTypes.LOCATIONS));
        setYears(getFilteredValues(filterTypes.YEAR));
        setMonths(getFilteredValues(filterTypes.MONTHS));
        setAvaliableStatuses(getFilteredValues(filterTypes.STATUS));
        setDays(getFilteredValues(filterTypes.DAY));
        setSortBy(getFilteredValues(filterTypes.SORTBY));
        if (isApprover) setContractors(filtersOptions?.Contractors?.length && [...filtersOptions.Contractors]);
        break;
      }
      case appAreas.EXPENSES: {
        setYears(getFilteredValues(filterTypes.YEAR));
        setAvaliableStatuses(getFilteredValues(filterTypes.STATUS));
        setMonths(getFilteredValues(filterTypes.MONTHS));
        setDays(getFilteredValues(filterTypes.DAY));
        setSortBy(getFilteredValues(filterTypes.SORTBY));
        if (isApprover) setContractors(filtersOptions.Contractors && [...filtersOptions.Contractors]);
        setClaims(filtersOptions?.ClaimNames && [...filtersOptions.ClaimNames]);
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    getListsForFilters();
  }, [isApprover, filtersOptions]);

  const scrollToLeft = () => {
    if (containerRef.current) {
      containerRef.current.scrollLeft = 0; // Scroll to the leftmost position
    }
  };

  useEffect(() => {
    if (width < responsiveSizeTimesheet) {
      const selected = filters.filter((filter) => {
        const key = filter.toLowerCase(); // Convert filter name to match value object keys
        return values[key] && values[key] !== ''; // Check if value exists and is not empty
      });

      const unselected = filters.filter((filter) => !selected.includes(filter));

      setReArrangedFilter([...selected, ...unselected]);
      scrollToLeft();
    }
  }, [values]);

  const getFilter = (filter) => {
    switch (filter) {
      case tAndEFilters.DAY:
        return (
          <DayFilter
            key="day"
            dateBackAction={changeHandlers.dateBackAction}
            dateForwardAction={changeHandlers.dateForwardAction}
            daysAvailability={days}
            day={values.day}
            changeDay={changeHandlers.handleDay}
            loading={filtersOptionsPending}
          />
        );
      case tAndEFilters.WEEK:
        return (
          <WeekFilter
            key="week"
            dateBackAction={changeHandlers.dateBackAction}
            dateForwardAction={changeHandlers.dateForwardAction}
            daysAvailability={days}
            week={values.start}
            changeWeek={changeHandlers.handleWeek}
          />
        );
      case tAndEFilters.MONTH:
        return (
          <MonthFilter
            key="month"
            filterMonths={months}
            years={years}
            selectedMonth={values.month}
            selectedYear={values.year}
            changeMonth={changeHandlers.handleMonth}
            changeYear={changeHandlers.handleYear}
            dateBackAction={changeHandlers.dateBackAction}
            dateForwardAction={changeHandlers.dateForwardAction}
          />
        );
      case tAndEFilters.CONTRACTOR:
        return (
          <ContractorFilter
            key="contractor"
            contractors={contractors}
            selectedContractor={values.contractor}
            changeContractor={changeHandlers.handleContractor}
            dateBackAction={changeHandlers.dateBackAction}
          />
        );
      case tAndEFilters.CLAIM:
        return (
          <ClaimFilter
            key="claims"
            claims={claims}
            selectedClaim={values.claim}
            changeClaim={changeHandlers.handleClaim}
            dateBackAction={changeHandlers.dateBackAction}
          />
        );
      case tAndEFilters.LOCATION:
        return (
          <LocationFilter
            key="location"
            locations={locations}
            selectedLocation={values.location}
            changeLocation={changeHandlers.handleLocation}
            dateBackAction={changeHandlers.dateBackAction}
          />
        );
      case tAndEFilters.STATUS:
        return (
          <StatusFilter
            key="status"
            selectedStatus={values.status || t('common.general.all')}
            changeStatus={changeHandlers.handleStatus}
            isApprover={isApprover}
            avaliableStatuses={avaliableStatuses}
            dateBackAction={changeHandlers.dateBackAction}
          />
        );
      case tAndEFilters.SORTBY:
        return (
          <CommonSelectFilter
            key="sortBy"
            type={tAndEFilters.SORTBY}
            text={t('common.filters.sortBy')}
            data={sortby}
            changeHandler={changeHandlers.handleSortBy}
            selected={values[tAndEFilters.SORTBY]}
            handleSingleReset={() => {
              changeHandlers.handleSortBy([TNE_SORT_BY.PERIOD]);
            }}
            values={values}
            footerHandler={footerHandler}
            isTextFullWidth
            swiperTitle={t('common.filters.sortBy')}
          />
        );
      case jobFilters.INDUSTRIES:
        return (
          <CommonSelectFilter
            key="industry"
            type={jobFilters.INDUSTRIES}
            text={t('common.filters.industry')}
            data={industries}
            changeHandler={changeHandlers}
            selected={values[jobFilters.INDUSTRIES]}
            values={values}
            resetIndividual={resetIndividual}
            count={jobFilterCounts}
            pendingCount={pendingFilterCount}
            footerHandler={footerHandler}
            isMultiSelect
          />
        );
      case jobFilters.CONTRACT_TYPES:
        return (
          <CommonSelectFilter
            key="contractTypes"
            type={jobFilters.CONTRACT_TYPES}
            text={t('common.filters.contracttype')}
            data={contractTypes}
            changeHandler={changeHandlers}
            selected={values[jobFilters.CONTRACT_TYPES]}
            values={values}
            resetIndividual={resetIndividual}
            count={jobFilterCounts}
            pendingCount={pendingFilterCount}
            footerHandler={footerHandler}
            isMultiSelect
          />
        );
      case jobFilters.FUNCTIONS:
        return (
          <CommonSelectFilter
            key="functions"
            type={jobFilters.FUNCTIONS}
            text={t('common.filters.function')}
            data={functions}
            changeHandler={changeHandlers}
            selected={values[jobFilters.FUNCTIONS]}
            values={values}
            resetIndividual={resetIndividual}
            count={jobFilterCounts}
            pendingCount={pendingFilterCount}
            footerHandler={footerHandler}
            isMultiSelect
          />
        );
      case jobFilters.LOCATION_TYPE:
        return (
          <CommonSelectFilter
            key="locationType"
            type={jobFilters.LOCATION_TYPE}
            text={t('common.filters.onsiteorremote')}
            data={workLocationTypes}
            changeHandler={changeHandlers}
            selected={values[jobFilters.LOCATION_TYPE]}
            values={values}
            resetIndividual={resetIndividual}
            count={jobFilterCounts}
            pendingCount={pendingFilterCount}
            footerHandler={footerHandler}
            isMultiSelect
          />
        );
      case jobFilters.DATE_POSTED:
        return (
          <DatePostedFilter
            key="datePosted"
            type={jobFilters.DATE_POSTED}
            changeDatePosted={changeHandlers}
            dateposted={filterPeriods}
            selected={values[jobFilters.DATE_POSTED]}
            values={values}
            count={jobFilterCounts}
            pendingCount={pendingFilterCount}
            footerHandler={footerHandler}
            resetIndividual={resetIndividual}
          />
        );
      default:
        break;
    }
    return false;
  };

  return (
    <div ref={containerRef} className={isContainerBg ? classes.containerWrapper : classes.container}>
      {reArrangedFilter.map((filter) => {
        return getFilter(filter);
      })}

      {reset && resetVisibility && (
        <MainButton action={() => resetHandler()} variant={buttonVariants.TEXT} size="xmedium">
          {t('common.filters.reset')}
        </MainButton>
      )}
    </div>
  );
}
